'use client';
import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  CatButton,
  CatIconArrowTriangularLeft,
  CatIconArrowTriangularRight,
  CatLayoutSection
} from 'components/blocks-components';
import { Conditional } from '@cat-ecom/pcc-components';
import PartsMenu from './parts-menu';
import styles from '../../parts-layout.module.scss';
import useURLParams from 'hooks/useURLParams';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import {
  CATEGORY_TYPES,
  GroupPartsType,
  MenuIndex,
  PartDetailsAndSchematicResponse,
  SearchParams,
  SystemDataResponseType
} from 'types/parts-diagram';

export interface SystemsDataProps {
  systemsData: SystemDataResponseType[];
  searchParams: Readonly<SearchParams>;
  searchParamsData: PartDetailsAndSchematicResponse;
}
const SystemAndComponents: FC<SystemsDataProps> = ({
  systemsData,
  searchParams,
  searchParamsData
}) => {
  const {
    isToggle,
    handleToggle,
    handlePartsContentCategory,
    handleSelectedPart
  } = usePartDiagram();
  const [verifyUrl, setVerifyUrl] = useState(false);
  const { deleteParams } = useURLParams();
  const partDiagramMenu = classNames(styles['parts-diagram-menu__sidebar'], {
    [`${styles['is-menu-collapsed']}`]: isToggle
  });
  const partDiagramBtn = classNames(styles['parts-diagram-menu__btn'], {
    [`${styles['parts-diagram-menu__btn__collapsed']}`]: isToggle
  });

  const getPartNumber = (
    sysId: string,
    compId: string,
    ieSysControlNumber: string,
    menuIndexs: MenuIndex[]
  ) => {
    const { data: { partNumber = '' } = {} } =
      (menuIndexs ?? []).filter((data) => {
        const {
          data: {
            componentId = '',
            systemId = '',
            ieSystemControlNumber = ''
          } = {}
        } = data || {};
        return (
          (sysId ? systemId === sysId : true) &&
          componentId === compId &&
          ieSystemControlNumber === ieSysControlNumber
        );
      })?.[0] || {};
    return partNumber;
  };

  const getComponentDetails = (sysId: string, compId: string) => {
    const { components } =
      (systemsData ?? []).filter((x) => x.id === sysId)?.[0] || {};
    const comp = (components ?? []).filter((x) => x.id === compId)?.[0];
    handleSelectedPart({ partType: 'component', sysId, compId: comp.id });
    handlePartsContentCategory(CATEGORY_TYPES.PARTSCARDS, comp);
  };

  const handleSearchParams = (menuIndexs: MenuIndex[]) => {
    const {
      componentId = '',
      ieSystemControlNumber = '',
      systemId = ''
    } = searchParams || {};
    if (!ieSystemControlNumber && componentId && systemId) {
      getComponentDetails(systemId, componentId);
      return;
    }
    if (!ieSystemControlNumber && !componentId && systemId) {
      handleSelectedPart({ partType: 'system', sysId: systemId });
      return;
    }
    const partNumber = getPartNumber(
      systemId,
      componentId,
      ieSystemControlNumber,
      menuIndexs
    );
    if (!partNumber || !searchParamsData) {
      return;
    }
    handlePartsContentCategory(
      CATEGORY_TYPES.PARTSDETAILS,
      null,
      searchParamsData
    );
    handleSelectedPart({ partNumber, ieSystemControlNumber });
  };

  useEffect(() => {
    const {
      componentId = '',
      ieSystemControlNumber = '',
      systemId = ''
    } = searchParams || {};
    const hasValidParams = !!(systemId && ieSystemControlNumber && componentId);
    if (hasValidParams) {
      const { components } =
        (systemsData ?? []).filter((x) => x.id === systemId)?.[0] || {};
      const { groupParts } =
        (components ?? []).filter((x) => x.id === componentId)?.[0] || {};
      const filteredGroupParts =
        (groupParts ?? []).filter(
          (x) => x.ieSystemControlNumber === ieSystemControlNumber
        )?.[0] || ({} as GroupPartsType);
      filteredGroupParts?.ieSystemControlNumber
        ? setVerifyUrl(false)
        : setVerifyUrl(true);
    } else {
      setVerifyUrl(false);
    }
    if (verifyUrl) {
      deleteParams('systemId');
      deleteParams('componentId');
      deleteParams('ieSystemControlNumber');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, systemsData, verifyUrl]);

  return (
    <CatLayoutSection className={partDiagramMenu}>
      <PartsMenu
        systemsData={systemsData}
        handleSearchParams={handleSearchParams}
      />
      <CatButton
        variant="primary"
        hideText
        size="md"
        onClick={handleToggle}
        className={partDiagramBtn}
      >
        <Conditional
          test={isToggle}
          fallback={<CatIconArrowTriangularLeft slot="before" />}
        >
          <CatIconArrowTriangularRight slot="before" />
        </Conditional>
      </CatButton>
    </CatLayoutSection>
  );
};

export default SystemAndComponents;
